import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  AttachMoney,
  DirectionsCar,
  QueryStats,
  TrendingUp,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { reportApi } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { APP_ROUTES } from '../routing/appRoutes';
import { getQueryErrorMessage } from '../utils/queryError';

const REPORT_TILES = [
  {
    id: 'revenue-report',
    icon: TrendingUp,
    tone: 'success' as const,
    titleKey: 'reports.revenue.title',
    titleDefault: 'Revenue Report',
    descriptionKey: 'reports.revenue.subtitle',
    descriptionDefault: 'Track completed-rental revenue over time and by vehicle class',
  },
  {
    id: 'vehicle-report',
    icon: DirectionsCar,
    tone: 'primary' as const,
    titleKey: 'reports.vehiclePerformance.title',
    titleDefault: 'Vehicle Performance Report',
    descriptionKey: 'reports.vehiclePerformance.subtitle',
    descriptionDefault: 'Revenue, operational costs and profit per vehicle',
  },
  {
    id: 'financial-reports',
    icon: QueryStats,
    tone: 'warning' as const,
    titleKey: 'financial.title',
    titleDefault: 'Total Profit Report',
    descriptionKey: 'financial.subtitle',
    descriptionDefault: 'Company-wide revenue, expenses and net profit',
  },
];

export const ReportsPage: React.FC = () => {
  const { t } = useTranslation();

  const range = useMemo(
    () => ({
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    }),
    []
  );

  const profitQuery = useQuery(
    ['totalProfitReport', range.startDate, range.endDate],
    () => reportApi.getTotalProfit(range.startDate, range.endDate)
  );
  const vehicleQuery = useQuery(
    ['vehiclePerformanceReport', range.startDate, range.endDate],
    () => reportApi.getVehiclePerformance(range.startDate, range.endDate)
  );

  const profitReport = profitQuery.data;
  const vehicleReport = vehicleQuery.data;
  const hasHubError = profitQuery.isError || vehicleQuery.isError;
  const hubError = profitQuery.error ?? vehicleQuery.error;

  const kpis = [
    {
      icon: TrendingUp,
      tone: 'success.main' as const,
      label: t('reports.hub.revenueThisMonth', 'Revenue This Month'),
      value: formatCurrency(profitReport?.totalRevenue),
    },
    {
      icon: AttachMoney,
      tone: (profitReport?.totalProfit ?? 0) >= 0 ? ('success.main' as const) : ('error.main' as const),
      label: t('reports.hub.profitThisMonth', 'Profit This Month'),
      value: formatCurrency(profitReport?.totalProfit),
    },
    {
      icon: DirectionsCar,
      tone: 'primary.main' as const,
      label: t('reports.hub.fleetSize', 'Fleet Size'),
      value: String(vehicleReport?.vehicles?.length ?? 0),
    },
  ];

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('pages.reportsTitle')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('reports.hub.subtitle', 'An overview of your business performance, with detailed reports below')}
      </Typography>
      {hasHubError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {getQueryErrorMessage(
            hubError,
            t('reports.hub.loadError', 'Some report data could not be loaded right now.')
          )}
        </Alert>
      ) : null}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {kpis.map((kpi) => {
          const KpiIcon = kpi.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={kpi.label}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <KpiIcon sx={{ color: kpi.tone }} />
                    <Typography variant="body2" color="text.secondary">
                      {kpi.label}
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ color: kpi.tone }}>
                    {kpi.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="h6" gutterBottom>
        {t('reports.hub.detailedReports', 'Detailed Reports')}
      </Typography>
      <Grid container spacing={3}>
        {REPORT_TILES.map((tile) => {
          const TileIcon = tile.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={tile.id}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea
                  component={RouterLink}
                  to={APP_ROUTES.company.group('reports', tile.id)}
                  sx={{ height: '100%', p: 2 }}
                >
                  <Stack spacing={1.5}>
                    <Avatar sx={{ bgcolor: `${tile.tone}.light`, color: `${tile.tone}.dark` }}>
                      <TileIcon />
                    </Avatar>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t(tile.titleKey, tile.titleDefault)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(tile.descriptionKey, tile.descriptionDefault)}
                    </Typography>
                  </Stack>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
